import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useSnackbar } from 'notistack';
import {
	setAlertActionCreator,
	setPageHeaderTextActionCreator,
	setTableStateActionCreator,
} from 'plugins/store/reducers/common.reducer';
import { setNumberRowsPerPageActionCreator } from 'plugins/store/reducers/settings.reducer';
import { addTaskActionCreator } from 'plugins/store/reducers/tasks.reducer';
import { can } from 'utils/permissions/permissionsChecker';
import permissionsMap from 'utils/permissions/permissionsMap';
import layoutNames from 'utils/enums/layout-names.enum';

// @material-ui/icons
import NotificationsNoneOutlined from '@material-ui/icons/NotificationsNoneOutlined';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import SnackMessage from 'components/Snackbar/SnackMessage';
import CustomPhoneLabel from 'components/CustomPhoneLabel/CustomPhoneLabel';
import ActionButtons from 'components/ActionButtons/ActionButtons';
import ReactTable from 'components/ReactTable/ReactTable';
import DateFormatResolver, { fullDateUsFormat } from 'resolvers/date-format.resolver';

import {
	searchPushNotifications,
	deletePushNotificationsList,
	deleteAllPushNotifications,
	downloadPushNotificationsCsvLogs,
} from 'services/push-notification.service';
import { mapNotificationsResult } from 'mappings/pushNotificationsMappings';

import { store } from 'plugins/store/store';
import CheckMark from 'assets/img/Checkmark.svg';
import Close from 'assets/img/Close.svg';

const PushNotifications = ({
	setAlert,
	setPageHeaderText,
	rowsPerPage,
	setRowsPerPage,
	defaultSearchQuery,
	defaultSortBy,
	defaultDateRangeFilter,
	defaultPageIndex,
	setTableState,
	addTask,
}) => {
	const { enqueueSnackbar } = useSnackbar();

	const columns = React.useMemo(
		() => [
			{
				Header: 'Name',
				accessor: 'type',
			},
			{
				Header: 'Calling from',
				accessor: 'phoneNumber',
				Cell: ({ value }) => (value ? (<CustomPhoneLabel value={value} />) : null),
			},
			{
				Header: 'Calling to',
				accessor: 'customerPhoneNumber',
				Cell: ({ value }) => (value ? (<CustomPhoneLabel value={value} />) : null),
			},
			{
				Header: 'Sent at',
				accessor: 'sentAt',
				sortDescFirst: true,
				Cell: ({ value }) => {
					return DateFormatResolver.resolve(value, fullDateUsFormat);
				}
			},
			{
				Header: 'Sent',
				accessor: 'isSent',
				Cell: ({ value }) => {
					return value
						? <img src={CheckMark} alt="Check Mark" />
						: <img src={Close} alt="Close" />
				},
				className: 'flag-align-right',
			}
		], []
	);

	const [data, setData] = React.useState([]);
	const [totalRecordsCount, setTotalRecordsCount] = React.useState(0);
	const [pageCount, setPageCount] = React.useState(0);
	const [loading, setLoading] = React.useState(true);
	const fetchIdRef = React.useRef(0)
	const viewName = 'Push Notifications';

	// component hook
	React.useEffect(() => {
		setPageHeaderText(viewName);

		return function componentWillUnmount() {
			setPageHeaderText('');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchData = React.useCallback(async ({ filterQuery, dateRangeFilter, pageIndex, pageSize, sortBy }) => {
		const fetchId = ++fetchIdRef.current;
		setLoading(true);
		if (fetchId === fetchIdRef.current) {
			const result = await searchPushNotifications({ filterQuery, dateRangeFilter, pageIndex, pageSize, sortBy });
			setLoading(false);
			const mappedResult = mapNotificationsResult(result);
			setData(mappedResult.data);
			setTotalRecordsCount(mappedResult.total);
			setPageCount(Math.ceil(mappedResult.total / pageSize));
		}
		setRowsPerPage({
			table: 'pushNotifications',
			rowsPerPage: pageSize
		});
		setTableState({
			table: 'pushNotifications',
			state: {
				dateRangeFilter,
				searchQuery: filterQuery,
				sortBy: sortBy,
				page: pageIndex
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDelete = (selection, refreshData) => {
		const idsToDelete = selection.map((item) => { return item.original.id });
		const deleteNotifications = {
			type: 'warning',
			title: 'Do you want to remove selected Push Notification(s)?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deletePushNotificationsList(idsToDelete);
					refreshData();
					enqueueSnackbar('Push Notification(s) successfully removed', {
						content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
					})
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
					})
				}
			},
			onCancel: () => {
				setAlert(null);
			}
		};
		store.dispatch(setAlertActionCreator(deleteNotifications));
	};

	const onDeleteAll = () => {
		const deleteAllNotifications = {
			type: 'warning',
			title: 'Remove all Push-notifications?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deleteAllPushNotifications();
					fetchData({
						filterQuery: '',
						dateRangeFilter: defaultDateRangeFilter,
						pageIndex: 0,
						pageSize: rowsPerPage,
						sortBy: defaultSortBy,
					});

					enqueueSnackbar('Push Notifications successfully removed', {
						content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
					})
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
					})
				}
			},
			onCancel: () => {
				setAlert(null);
			}
		};
		setAlert(deleteAllNotifications);
	};

	const downloadCsv = async () => {
		try {
			const task = await downloadPushNotificationsCsvLogs();
			addTask(task);
			enqueueSnackbar('The File will be downloaded in a few minutes. You can continue working with the Portal in the meantime', {
				content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
			});
		} catch (error) {
			enqueueSnackbar(error.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	const isClearSearchAndFilters = () => {
		if (
			defaultDateRangeFilter?.from !== null
			|| defaultDateRangeFilter?.to !== null
			|| defaultSearchQuery !== ''
		) {
			return false;
		}
		return true;
	};

	const actionItems = [];

	if (can(permissionsMap.pushNotificationsLog.items.downloadCsv.read)) {
		actionItems.push({
			label: 'Download .csv',
			fn: downloadCsv,
			tooltip: 'Download push-notifications for the last 30 days',
		});
	}

	if (can(permissionsMap.pushNotificationsLog.items.pushNotificationsLog.delete)) {
		const tooltip = isClearSearchAndFilters() ? '' : 'Reset filtering to remove all records';
		const disabled = !isClearSearchAndFilters();
		actionItems.push({
			label: 'Remove All',
			fn: onDeleteAll,
			tooltip,
			disabled,
		});
	}

	const actionButtons = <ActionButtons items={actionItems} />;

	return (
		<GridContainer>
			<GridItem xs={12} sm={12} md={12}>
				<Card>
					<CardHeader style={{ position: 'absolute' }} color='primary' icon>
						<CardIcon color='primary'>
							<NotificationsNoneOutlined />
						</CardIcon>
					</CardHeader>
					<CardBody>
						<GridContainer>
							<GridItem xs={12} sm={12} md={12}>
								<ReactTable
									rowsWithSelection={can(permissionsMap.pushNotificationsLog.items.pushNotificationsLog.delete)}
									isAllowDelete={can(permissionsMap.pushNotificationsLog.items.pushNotificationsLog.delete)}
									columns={columns}
									data={data}
									fetchData={fetchData}
									rowsPerPage={rowsPerPage}
									defaultSearchQuery={defaultSearchQuery}
									defaultSortBy={defaultSortBy}
									defaultPageIndex={defaultPageIndex}
									loading={loading}
									pageCount={pageCount}
									totalRecordsCount={totalRecordsCount}
									detailsPath={`${layoutNames.portal}/push-notifications/details`}
									allowDeleteAll
									onDelete={onDelete}
									isPageRootComponent
									addRecordComponent={actionButtons}
									defaultDateRangeFilter={defaultDateRangeFilter}
								/>
							</GridItem>
						</GridContainer>
					</CardBody>
				</Card>
			</GridItem>
		</GridContainer>
	)
}

const mapStateToProps = ({
	common: {
		tablesState: {
			pushNotifications: {
				searchQuery,
				sortBy,
				dateRangeFilter,
				page,
			} }
	},
	settings: { rowsPerPage } }) => {
	return {
		rowsPerPage: rowsPerPage.pushNotifications,
		defaultSearchQuery: searchQuery,
		defaultSortBy: sortBy,
		defaultDateRangeFilter: dateRangeFilter,
		defaultPageIndex: page,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch),
	setRowsPerPage: bindActionCreators(
		setNumberRowsPerPageActionCreator,
		dispatch
	),
	setTableState: bindActionCreators(
		setTableStateActionCreator,
		dispatch
	),
	addTask: bindActionCreators(
		addTaskActionCreator,
		dispatch,
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(PushNotifications);
